import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { restrictedCaseApi } from '../../api/restricted-cases';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { FolderOpen } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const CASE_TYPE_LABELS: Record<string, string> = { disciplinary: 'Disciplinary', grievance: 'Grievance', safeguarding: 'Safeguarding', psea: 'PSEA', other: 'Other' };
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  investigation: 'Investigation', decision_pending: 'Decision Pending', closed: 'Closed', cancelled: 'Cancelled',
};

/**
 * Restricted / Confidential. The list here shows ONLY what the
 * server has already decided this user may see (Section 41) - this
 * page never receives, and therefore never has to hide, a case the
 * viewer isn't authorised for.
 */
export function RestrictedCasesPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  const allQuery = useQuery({ queryKey: ['restricted-cases-all'], queryFn: () => restrictedCaseApi.listAll() });
  const detailQuery = useQuery({ queryKey: ['restricted-case-detail', selectedCaseId], queryFn: () => restrictedCaseApi.getById(selectedCaseId!), enabled: !!selectedCaseId });

  const { register, handleSubmit, watch, reset } = useForm<Record<string, string>>();
  const caseType = watch('caseType');

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => restrictedCaseApi.create({
      caseType: v.caseType, reporterType: v.reporterType, reporterIdentityStatus: v.reporterIdentityStatus || 'identified',
      incidentDate: v.incidentDate || undefined, incidentLocation: v.incidentLocation || undefined,
      subjectEmployeeId: v.subjectEmployeeId ? Number(v.subjectEmployeeId) : undefined,
      summary: v.summary, description: v.description || undefined, otherExplanation: v.otherExplanation || undefined,
    }),
    onSuccess: (result) => { showSuccess(`Case recorded - ${result.data.caseReference}`); queryClient.invalidateQueries({ queryKey: ['restricted-cases-all'] }); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record case.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <PageHeader
        title="Restricted Cases"
        description="This information is confidential and may only be accessed and used by authorised personnel."
        actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Report a Case'}</button>}
      />

      {showForm && (
        <section className="card">
          <h2>Report a Case</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>
              Case Type
              <select {...register('caseType', { required: true })}>
                <option value="">- select -</option>
                <option value="disciplinary">Disciplinary</option>
                <option value="grievance">Grievance</option>
                <option value="safeguarding">Safeguarding</option>
                <option value="psea">PSEA</option>
                <option value="other">Other</option>
              </select>
            </label>
            {caseType === 'other' && <label>Explanation (required for Other)<input {...register('otherExplanation')} /></label>}

            <label>
              Reporter Type
              <select {...register('reporterType', { required: true })}>
                <option value="">- select -</option>
                <option value="staff">Staff</option><option value="volunteer">Volunteer</option><option value="consultant">Consultant</option>
                <option value="partner">Partner</option><option value="contractor">Contractor</option><option value="supplier">Supplier</option>
                <option value="community_member">Community Member</option><option value="beneficiary">Beneficiary</option><option value="other">Other</option>
              </select>
            </label>
            <label>
              Reporter Identity
              <select {...register('reporterIdentityStatus')}>
                <option value="identified">Identified</option>
                <option value="confidential">Confidential</option>
                <option value="anonymous">Anonymous</option>
              </select>
            </label>

            <label>Incident Date<input type="date" {...register('incidentDate')} /></label>
            <label>Incident Location<input {...register('incidentLocation')} /></label>
            {(caseType === 'disciplinary' || caseType === 'safeguarding') && (
              <label>Subject Employee ID (if the allegation concerns a staff member)<input type="number" {...register('subjectEmployeeId')} /></label>
            )}

            <label>Summary<input {...register('summary', { required: true })} placeholder="Brief, neutral summary" /></label>
            <label>What Happened? (detailed)<textarea rows={4} {...register('description')} /></label>

            <div className="form-actions"><button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Recording…' : 'Record Case'}</button></div>
          </form>
        </section>
      )}

      <section className="card">
        <h2>Cases Visible to You</h2>
        {allQuery.data && (
          <DataTable
            rows={allQuery.data.data}
            rowKey={(c) => c.id}
            emptyMessage="No restricted cases found."
            columns={[
              { header: 'Reference', render: (c) => c.case_reference },
              { header: 'Type', render: (c) => CASE_TYPE_LABELS[c.case_type] ?? c.case_type },
              { header: 'Status', render: (c) => <StatusBadge status={c.status} label={STATUS_LABELS[c.status]} /> },
              { header: 'Reported', render: (c) => new Date(c.reported_at).toLocaleDateString() },
              { header: '', render: (c) => <IconActionButton icon={FolderOpen} label="Open Case" onClick={() => setSelectedCaseId(c.id)} /> },
            ]}
          />
        )}
      </section>

      {selectedCaseId && detailQuery.data && (
        <CaseWorkspace caseId={selectedCaseId} data={detailQuery.data} onClose={() => setSelectedCaseId(null)} />
      )}
    </div>
  );
}

function CaseWorkspace({ caseId, data, onClose }: { caseId: number; data: NonNullable<ReturnType<typeof restrictedCaseApi.getById> extends Promise<infer T> ? T : never>; onClose: () => void }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const record = data.data;

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['restricted-case-detail', caseId] }); queryClient.invalidateQueries({ queryKey: ['restricted-cases-all'] }); };

  const submitMutation = useMutation({ mutationFn: () => restrictedCaseApi.submit(caseId), onSuccess: () => { showSuccess('Submitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const returnMutation = useMutation({ mutationFn: (comment: string) => restrictedCaseApi.return(caseId, comment), onSuccess: () => { showSuccess('Returned for redraft.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const investigationMutation = useMutation({
    mutationFn: (v: { startDate: string; findings: string }) => restrictedCaseApi.startInvestigation(caseId, v),
    onSuccess: () => { showSuccess('Investigation recorded.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const decisionMutation = useMutation({
    mutationFn: (v: { outcome: string; summary: string }) => restrictedCaseApi.recordDecision(caseId, v.outcome, v.summary),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const closeMutation = useMutation({ mutationFn: (reason: string) => restrictedCaseApi.close(caseId, reason), onSuccess: () => { showSuccess('Case closed.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const reopenMutation = useMutation({ mutationFn: (reason: string) => restrictedCaseApi.reopen(caseId, reason), onSuccess: () => { showSuccess('Case reopened.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  const investigationForm = useForm<{ startDate: string; findings: string }>();
  const decisionForm = useForm<{ outcome: string; summary: string }>();

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="page-header">
        <h2>{record.case_reference} - {CASE_TYPE_LABELS[record.case_type]}</h2>
        <button className="link-button" onClick={onClose}>Close Workspace</button>
      </div>
      <p className="muted">Confidentiality: {record.confidentiality_level.replace('_', ' ')} · Status: {STATUS_LABELS[record.status] ?? record.status}</p>

      <h3>Overview</h3>
      <p>{record.summary}</p>
      {record.description && <p className="muted">{record.description}</p>}
      {record.subject_employee_id && (
        <div className="project-header-grid">
          <StaticField label="Subject Position (at time of report)" value={record.subject_position_snapshot ?? '-'} />
          <StaticField label="Subject Department (at time of report)" value={record.subject_department_snapshot ?? '-'} />
        </div>
      )}

      {record.status === 'draft' && <div className="form-actions"><button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>Submit</button></div>}

      {(record.status === 'submitted' || record.status === 'under_review') && (
        <div className="form-actions">
          <button onClick={() => { const c = window.prompt('Return comment (required):'); if (c) returnMutation.mutate(c); }}>Return for Redraft</button>
        </div>
      )}

      {record.status === 'submitted' && (
        <>
          <h3>Start Investigation</h3>
          <form onSubmit={investigationForm.handleSubmit((v) => investigationMutation.mutate(v))}>
            <label>Start Date<input type="date" {...investigationForm.register('startDate', { required: true })} /></label>
            <label>Findings<textarea rows={2} {...investigationForm.register('findings')} /></label>
            <div className="form-actions"><button type="submit" disabled={investigationMutation.isPending}>Record Investigation</button></div>
          </form>
        </>
      )}

      {record.status === 'investigation' && (
        <>
          <h3>Record Decision</h3>
          <form onSubmit={decisionForm.handleSubmit((v) => decisionMutation.mutate(v))}>
            <label>Outcome<input {...decisionForm.register('outcome', { required: true })} placeholder="e.g. Substantiated, No Action, Referred" /></label>
            <label>Decision Summary<textarea rows={2} {...decisionForm.register('summary', { required: true })} /></label>
            <div className="form-actions"><button type="submit" disabled={decisionMutation.isPending}>Record Decision</button></div>
          </form>
        </>
      )}

      {record.status === 'decision_pending' && (
        <div className="form-actions">
          <button onClick={() => { const r = window.prompt('Closure reason:'); if (r) closeMutation.mutate(r); }}>Close Case</button>
        </div>
      )}

      {record.status === 'closed' && (
        <div className="form-actions">
          <button onClick={() => { const r = window.prompt('Reason for reopening:'); if (r) reopenMutation.mutate(r); }}>Reopen Case</button>
        </div>
      )}

      {record.decision_summary && <p className="muted">Decision: <strong>{record.outcome}</strong> - {record.decision_summary}</p>}
    </section>
  );
}

function StaticField({ label, value }: { label: string; value: string }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}
